import { Component } from '@angular/core';
import {
  AbstractControl,
  Validators,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { SimpleSearchComponent } from 'src/app/base/components/abstract/simple/simple-search.component';
import { DataAction } from 'src/app/base/enum/data-action.enum';
import { DialogReturnData } from 'src/app/base/models/dialog-return-data.model';
import { Option } from 'src/app/base/models/option.model';
import { TableColumn } from 'src/app/base/models/table-column.model';
import { AlternativePartInquiryService } from '../../services/alternative-part-inquiry.service';
import {
  AlternativeHeader,
  AlternativeDetail,
  AlternativeDetailCondition,
} from '../../models/alternative-part-inquiry.model';
import { takeUntil, Subject, switchMap, filter, firstValueFrom } from 'rxjs';
import { StockSummaryService } from '../../services/stock-summary.service';
import { CommonService } from '../../services/common.service';

@Component({
  selector: 'app-alternative-part-inquiry',
  templateUrl: './alternative-part-inquiry.component.html',
  styleUrls: ['./alternative-part-inquiry.component.scss'],
})
export class AlternativePartInquiryComponent extends SimpleSearchComponent {
  override rowDataKeyName: string = 'itemNumber';
  override rowDataDeleteHintFieldName: string = 'itemNumber';
  override componentClassName: string = 'AlternativePartInquiryComponent';
  // OU AutoComplete Dropdown 的 Suggestions 資料 (篩選後的資料)
  ouSuggestions: Option[] = [];

  // Item AutoComplete Dropdown 的 Suggestions 資料 (篩選後的資料)
  itemSuggestions: Option[] = [];

  // OU AutoComplete Dropdown 值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private readonly ouSubject$ = new Subject<string | undefined>();

  // Item AutoComplete Dropdown 值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private readonly itemSubject$ = new Subject<string | undefined>();

  override showActions: boolean = false;

  override showNoDataFoundMessage: boolean = false;

  queryId: string = '';

  alternativeHeaderList: AlternativeHeader[] = [];

  // 每個欄位 checkbox 控制
  groupSelection: boolean[] = [];

  tableExpandedColumns: TableColumn[] = [];

  expandedRows: { [key: string]: boolean } = {};

  constructor(
    private readonly stockSummaryService: StockSummaryService,
    private readonly commonService: CommonService,
    private readonly alternativePartInquiryService: AlternativePartInquiryService
  ) {
    super();
  }

  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  override async doQuery(): Promise<boolean> {
    const itemNumber = this.formControl('itemNumber').getRawValue() ?? '';
    const vendorItem = this.formControl('vendorItem').getRawValue() ?? '';
    const ouId = this.formControl('ouId').getRawValue()?.value ?? '';
    
    const response = await firstValueFrom(
      this.alternativePartInquiryService
        .getAlternativeHeader(itemNumber, vendorItem, ouId)
        .pipe(takeUntil(this._destroying$))
    );

    if (
      !response.failure &&
      response.queryId &&
      response.responseData?.alternativePartList?.length > 0
    ) {
      this.detailDatas = response.responseData?.alternativePartList ?? [];
      this.queryId = response.queryId ?? '';
      this.expandedRows = {};
    } else {
      this.detailDatas = [];
      this.queryId = response.queryId;
      if (response.failure)
        this.systemMessageService.error(response.exceptionMsg);
    }
    return true;
  }
  override afterSelectedRowDataCountChange(selectedDataCount: number): void {
    return;
  }
  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }
  override initExtraDetailMenu(): void {
    return;
  }
  override setExtraDetailMenuStatus(): void {
    return;
  }
  override initTableColumns(): void {
    this.tableColumns = [
      {
        header: 'field.item-number',
        field: 'itemNumber',
        sortable: true,
      },
      {
        header: 'field.vendor-item',
        field: 'vendorItem',
        sortable: true,
      },
      {
        header: 'field.brand',
        field: 'brand',
        sortable: true,
      },
      {
        header: 'field.total-stock-qty',
        field: '',
        fields: ['groupTotalStock','ouTotalStock'],
        customField: true,
        sortable: true,
      },
      {
        header: 'field.free-stock-qty',
        field: '',
        fields: ['groupFreeStock','ouFreeStock'],
        customField: true,
        sortable: true,
      },
      {
        header: 'field.share-stock-qty',
        field: '',
        fields: ['groupShareStock','ouShareStock'],
        customField: true,
        sortable: true,
      },
    ];

  }
  override initDialogConfig(dataAction: DataAction): void {
    return;
  }
  override initExtraRowActionMenu(): void {
    return;
  }
  override setExtraRowActionMenuStatus(): void {
    return;
  }
  override doExtraDetailDialogClose(returnData: DialogReturnData): void {
    return;
  }
  override async doRowActionDelete(): Promise<boolean> {
    return true;
  }
  override afterDetailTabActiveItemChange(): void {
    return;
  }
  override initFormGroup(): void {
    // 依據初次載入的資料當作查詢條件
    this.formGroup = this.formBuilder.nonNullable.group({
        uuid: '',
        ouId: [ '', Validators.required],
        itemNumber: '',
        vendorItem: '',
      }, {
        validators: [this.itemOrVendorRequiredValidator()],
      }
    );
  }
  override initStaticOptions(): void {
    return;
  }
  override async initFormData(): Promise<any> {
    if (this.dynamicDialogConfig.data?.uuid) {
      // 如果有 uuid，表示是從其他頁面點擊進來的，直接查詢
      await this.alternativePartInquiry();
    }
    return true;
  }
  override afterLangChange(): void {
    return;
  }
  override afterFormDataLoadSuccess(): void {
    // 避免點擊重置按鈕後帶入originalFormRawValue所以改在這裡處理
    if (this.dynamicDialogConfig.data?.uuid) { 
      this.stockSummaryService.getOrgs(String(this.dynamicDialogConfig.data.ouId))
        .subscribe((data) => {
          this.formGroup.patchValue({
          uuid: this.dynamicDialogConfig.data?.uuid ?? '',
          ouId: data[0] ?? '',
          itemNumber: this.dynamicDialogConfig.data?.itemNumber ?? ''
        });
      });
      this.dynamicDialogConfig.data = {};
    }
  }
  override afterFormDataLoadFail(): void {
    return;
  }
  override initFormControlDisabled(): void {
    return;
  }

  /**
   * Item Number & Vendor Item 共用驗證器
   */
  itemOrVendorRequiredValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const itemNumberControl = group.get('itemNumber');
      const vendorItemControl = group.get('vendorItem');

      const itemNumber = itemNumberControl?.value;
      const vendorItem = vendorItemControl?.value;

      const errorsItemNumber: ValidationErrors = {};
      const errorsVendorItem: ValidationErrors = {};

      let hasError = false;

      // 邏輯 1：兩欄擇一填寫
      if ((!itemNumber && !vendorItem) || (itemNumber && vendorItem)) {
        errorsItemNumber['itemRequired'] = true;
        errorsVendorItem['itemRequired'] = true;
        hasError = true;
      }

      // 邏輯 2：不可包含 %
      if (itemNumber?.includes('%')) {
        errorsItemNumber['itemPercentage'] = true;
        hasError = true;
      }
      if (vendorItem?.includes('%')) {
        errorsVendorItem['itemPercentage'] = true;
        hasError = true;
      }

      // 邏輯 3：長度必須 ≥ 3
      if (itemNumber && itemNumber.length < 3) {
        errorsItemNumber['itemLength'] = true;
        hasError = true;
      }
      if (vendorItem && vendorItem.length < 3) {
        errorsVendorItem['itemLength'] = true;
        hasError = true;
      }

      // 設定錯誤（如果有）
      if (Object.keys(errorsItemNumber).length > 0) {
        itemNumberControl?.setErrors(errorsItemNumber);
      } else {
        itemNumberControl?.setErrors(null);
      }

      if (Object.keys(errorsVendorItem).length > 0) {
        vendorItemControl?.setErrors(errorsVendorItem);
      } else {
        vendorItemControl?.setErrors(null);
      }
      // group 本身回傳 null（或有需要也可設群組錯）
      return hasError ? { itemGroupInvalid: true } : null;
    };
  }

  searchOuSuggestions(event: { query: any }): void {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.ouSubject$.observed) {
      this.ouSubject$
        .pipe(
          switchMap((keyword) => {
            return this.stockSummaryService.getOrgs(keyword);
          }),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => {
          this.ouSuggestions = data;
        });
    }
    this.ouSubject$.next(event.query);
  }

  searchItemSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.itemSubject$.observed) {
      this.itemSubject$
        .pipe(
          filter(Boolean), // 過濾 null、undefined、'' 等 falsy 值（如需）
          switchMap((keyword: string) => this.commonService.getItems(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => {
            this.itemSuggestions = data;
            console.log('this.itemSuggestions.length =', this.itemSuggestions.length);
          },
          error: (err) => {
            console.error('getItems failed:', err);
            this.itemSuggestions = [];
          }
        });
    }

    this.itemSubject$.next(event.query);
  }

  async alternativePartInquiry(): Promise<void> {
    this.panelCollapsed = false;
    this.submitted = true;
    if (this.formGroup.valid || this.dynamicDialogConfig.data?.uuid) {
      await new Promise<void>((resolve) => {
        this.waitAutoCompleteCompleted(() => {
          (async () => {
            try {
              this.loadingMaskService.show();
              let itemNumber, vendorItem, orgId;
              if(this.dynamicDialogConfig.data?.uuid) {
                itemNumber = this.dynamicDialogConfig.data.itemNumber ?? '';
                vendorItem = this.dynamicDialogConfig.data.vendorItem ?? '';
                orgId = this.dynamicDialogConfig.data.ouId ?? '';
              }else{
                itemNumber = this.formControl('itemNumber').getRawValue() ?? '';
                vendorItem = this.formControl('vendorItem').getRawValue() ?? '';
                orgId = this.formControl('ouId').getRawValue()?.value ?? '';
              }

              const response = await firstValueFrom(
                this.alternativePartInquiryService
                  .getAlternativeHeader(itemNumber, vendorItem, orgId)
                  .pipe(takeUntil(this._destroying$))
              );

              if (
                !response.failure &&
                response.queryId
              ) {
                this.detailDatas =
                  response.responseData?.alternativePartList ?? [];
                this.groupSelection = Array(this.alternativeHeaderList.length).fill(false);
                this.queryId = response.queryId ?? '';
                this.expandedRows = {};
                this.changeDetectorRef.detectChanges();
              } else {
                this.queryId = response.queryId;
                if (response.failure) {
                  this.systemMessageService.error(response.exceptionMsg);
                }
              }
            } catch (err) {
              console.error('查詢錯誤', err);
            } finally {
              this.loadingMaskService.hide();
              resolve();
            }
          })();
        });
      });
    }
  }

  headerSearchReset() {
    this.alternativeHeaderList = [];
    this.groupSelection = [];
    this.queryId = '';
    this.detailDatas = [];
    this.expandedRows = {};
    this.dynamicDialogConfig.data = {};
  }

  expandViewStatus(rowData: AlternativeDetail): boolean {
    return rowData.stock.length > 0 && this.queryId !== '';
  }

  onCheckboxChange(index: number, checked: boolean): void {
    this.groupSelection[index] = checked;
    // 檢查是否超過 5 個勾選
    const selectedCount = this.groupSelection.filter((x) => x).length;
    if (selectedCount > 5) {
      // 超過 5 個，取消這次勾選
      setTimeout(() => {
        this.groupSelection[index] = false;
        this.changeDetectorRef.detectChanges(); // 確保 UI 更新
      });

      // 提示最多只能選擇 5 個
      this.systemMessageService.warn('message.validation.select-up-max-count', {
        maxCount: 5,
      });
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
}
